import { useMemo, useState, type ReactNode } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Form } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { useAppContext } from '@/Main';
import { PANEL_FONT_FAMILY } from '../../utils/panel-font';
import {
  DASHBOARD_PLOT_HEIGHT,
  chartChrome,
  competitorColors,
  escapeHtml,
} from '../../utils/competition-chart';
import { competitorBrandKey } from '../../utils/dashboard-competitors';
import type { ProjectVisibilityTopBrand } from '../../types';
import { DashboardEmptyNote } from './DashboardEmptyNote';
import { ChartLegend, useLegendHidden } from './competition/ChartLegend';

const METRIC_OPTIONS = [
  { key: 'shareOfVoice', label: 'سهم از دیده‌شدن' },
  { key: 'visibilityScore', label: 'دیده‌شدن در AI' },
] as const;
type MetricKey = (typeof METRIC_OPTIONS)[number]['key'];

/** Share-of-voice / AI-visibility bars for top competitors; each competitor gets its own color. */
export function TopCompetitorsShareOfVoiceCard({
  competitors,
  plotHeight = DASHBOARD_PLOT_HEIGHT,
  footer,
  hideHeader,
}: {
  competitors: ProjectVisibilityTopBrand[];
  plotHeight?: number;
  footer?: ReactNode;
  hideHeader?: boolean;
}) {
  const {
    config: { isDark, themeVariant },
  } = useAppContext() as { config: { isDark: boolean; themeVariant?: string } };
  const { hidden, toggle } = useLegendHidden();
  const [metric, setMetric] = useState<MetricKey>('shareOfVoice');
  const metricLabel = METRIC_OPTIONS.find((item) => item.key === metric)!.label;

  const colors = competitorColors();
  const colorFor = (brand: ProjectVisibilityTopBrand) =>
    colors[competitors.indexOf(brand) % colors.length];

  const visibleCompetitors = useMemo(
    () => competitors.filter((brand) => !hidden.has(competitorBrandKey(brand))),
    [competitors, hidden],
  );

  const option = useMemo(() => {
    if (!visibleCompetitors.length) return null;
    const chrome = chartChrome();
    const maxShare = Math.max(0, ...competitors.map((brand) => brand[metric]));
    const axisMax = Math.min(100, Math.max(10, Math.ceil(maxShare / 5) * 5));
    // Category y-axis draws bottom-up; reverse so the largest share sits on top.
    const ordered = [...visibleCompetitors].reverse();

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow', shadowStyle: { color: chrome.splitLine, opacity: 0.4 } },
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: chrome.tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (params: Array<{ dataIndex: number }>) => {
          if (!params.length) return '';
          const brand = ordered[params[0].dataIndex];
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${chrome.tooltipText}">
            <div style="font-weight:600">${escapeHtml(brand.canonicalName)}</div>
            <div style="margin-top:4px">${metricLabel}: <strong>${brand[metric]}%</strong></div></div>`;
        },
      },
      grid: { left: 84, right: 16, top: 12, bottom: 32, containLabel: false },
      xAxis: {
        type: 'value',
        min: 0,
        max: axisMax,
        splitNumber: 4,
        splitLine: { lineStyle: { color: chrome.splitLine } },
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          formatter: '{value}%',
        },
      },
      yAxis: {
        type: 'category',
        data: ordered.map((brand) => brand.canonicalName),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          show: true,
          interval: 0,
          hideOverlap: false,
          align: 'left',
          margin: 82,
          color: chrome.axisLabel,
          fontSize: 11,
          fontFamily: PANEL_FONT_FAMILY,
          overflow: 'truncate',
          width: 80,
        },
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 18,
          barCategoryGap: '35%',
          itemStyle: { borderRadius: [0, 4, 4, 0] },
          data: ordered.map((brand) => ({
            value: brand[metric],
            itemStyle: { color: colorFor(brand) },
          })),
        },
      ],
    };
  }, [competitors, visibleCompetitors, metric, metricLabel, isDark, themeVariant]);

  const metricPicker = (
    <Form.Select
      size="sm"
      className="fs--2"
      style={{ width: '9rem' }}
      aria-label="انتخاب شاخص نمودار"
      value={metric}
      onChange={(event) => setMetric(event.target.value as MetricKey)}
    >
      {METRIC_OPTIONS.map((option) => (
        <option key={option.key} value={option.key}>
          {option.label}
        </option>
      ))}
    </Form.Select>
  );

  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      {hideHeader ? null : (
        <FalconCardHeader
          light
          titleTag="h6"
          className="py-3"
          title="سهم از دیده‌شدن رقبای برتر"
          endEl={metricPicker}
        />
      )}
      <Card.Body className="pt-3 d-flex flex-column flex-grow-1">
        {hideHeader ? <div className="mb-2">{metricPicker}</div> : null}
        <div className="flex-grow-1 d-flex align-items-center" dir="ltr">
          {option ? (
            <ReactECharts
              key={`${isDark ? 'dark' : 'light'}-${themeVariant ?? 'primary'}`}
              option={option}
              notMerge
              className="w-100"
              style={{ height: plotHeight }}
              opts={{ renderer: 'svg' }}
            />
          ) : (
            <DashboardEmptyNote minHeight={plotHeight}>
              {competitors.length === 0
                ? 'در این بازه رقیبی در پاسخ‌های AI دیده نشده.'
                : 'همه برندها از نمودار پنهان شده‌اند.'}
            </DashboardEmptyNote>
          )}
        </div>
        <ChartLegend
          onToggle={toggle}
          items={competitors.map((brand) => ({
            id: competitorBrandKey(brand),
            label: brand.canonicalName,
            color: colorFor(brand),
            hidden: hidden.has(competitorBrandKey(brand)),
          }))}
        />
      </Card.Body>
      {footer}
    </Card>
  );
}
